// "Los más pedidos" de /services como carrusel (Diego, 03-11-2026). Mismo alto
// que la grilla: 4 tarjetas a la vez en escritorio, 2 en el teléfono; las demás
// entran deslizando. Sin librería: un contenedor con overflow-x y scroll-snap, y
// el desplazamiento automático animando scrollLeft.
//
// Por qué tanto cuidado con que pare: la tarjeta es un enlace, y si se mueve
// bajo el cursor la gente entra al servicio equivocado. Para:
//   - con el mouse encima (solo mouse: el toque emula un "encima" que no se va);
//   - con el foco de teclado adentro (y trae la tarjeta enfocada a la vista);
//   - mientras el dedo toca, y unos segundos después de soltar.
// prefers-reduced-motion: no se desplaza solo; queda una fila que se mueve a mano.
//
// Botón de pausa (03-11-2026): visible, con el estado en texto y manejable con
// teclado. La pausa es PEGAJOSA: queda parado hasta que la persona lo vuelva a
// arrancar; no se reanuda al sacar el mouse ni al soltar el foco. Publicamos una
// política de accesibilidad con WCAG 2.1 AA como objetivo, y 2.2.2 pide una forma
// de pausar lo que se mueve solo más de 5 s.
//
// El bucle: las tarjetas van dos veces; al llegar a la primera copia se vuelve
// un juego entero atrás, que se ve igual. Las copias van con aria-hidden e inert
// y no toman foco: un lector de pantalla no lee los servicios dos veces.
//
// scroll-snap y el desplazamiento automático se pelean (el snap devuelve la
// posición a la tarjeta más cercana y el carrusel se traba), así que el snap va
// apagado mientras corre solo y se prende cuando lo mueve la persona (dedo,
// rueda, arrastre). NO se prende al pasar el mouse: prenderlo reacomoda la fila,
// y eso es mover la tarjeta bajo el cursor.
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Tarjeta } from './ServiceGrid';

const VELOCIDAD_PX_S = 18;        // una tarjeta de escritorio (~250 px) tarda ~14 s en pasar
const UMBRAL_ARRASTRE_PX = 6;     // más que esto es arrastre: el click se cancela
const ESPERA_TRAS_TOQUE_MS = 2500;
const GAP_PX = 12;                // gap: 1.5 de MUI
const VISIBLES = { xs: 2, md: 4 };

const anchoItem = {
  xs: `0 0 calc((100% - ${GAP_PX * (VISIBLES.xs - 1)}px) / ${VISIBLES.xs})`,
  md: `0 0 calc((100% - ${GAP_PX * (VISIBLES.md - 1)}px) / ${VISIBLES.md})`,
};

const font = "'Montserrat', sans-serif";
const consultaMovimiento = () => (window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null);

export default function CarruselDestacados({ services, esAdmin = false, onEditar, onEliminar, titulo, tituloSx }) {
  const ref = useRef(null);
  const primeraCopia = useRef(null);
  // Con 4 o menos entran todos en escritorio: no hay bucle ni movimiento.
  const enBucle = services.length > VISIBLES.md;
  // La pausa del botón vive en un ref para que la lea el bucle de animación sin
  // reiniciar el efecto; el estado es para pintar el botón.
  const [pausado, setPausado] = useState(false);
  const pausadoRef = useRef(false);
  const alternarPausa = () => { pausadoRef.current = !pausadoRef.current; setPausado(pausadoRef.current); };
  const [reducido, setReducido] = useState(() => { const mq = consultaMovimiento(); return !!(mq && mq.matches); });
  useEffect(() => {
    const mq = consultaMovimiento();
    if (!mq || !mq.addEventListener) return undefined;
    const cambia = () => setReducido(mq.matches);
    mq.addEventListener('change', cambia);
    return () => mq.removeEventListener('change', cambia);
  }, []);
  const seMueve = enBucle && !reducido; // si nada se mueve solo, no hay botón

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const mq = consultaMovimiento();
    const reducido = () => !!(mq && mq.matches);
    const snap = (on) => { el.style.scrollSnapType = on ? 'x mandatory' : 'none'; };
    // Ancho de un juego de tarjetas: de la primera original a la primera copia.
    const anchoJuego = () => (enBucle && primeraCopia.current && el.firstElementChild
      ? primeraCopia.current.offsetLeft - el.firstElementChild.offsetLeft : 0);

    const estado = { mouse: false, foco: false, toque: false, arrastre: false, esperaHasta: 0 };
    const quieto = () => pausadoRef.current || estado.mouse || estado.foco || estado.toque || estado.arrastre
      || performance.now() < estado.esperaHasta;

    let pos = el.scrollLeft;
    let ultimoT = null;
    let raf = 0;
    snap(!enBucle || reducido());

    const paso = (t) => {
      raf = requestAnimationFrame(paso);
      const dt = ultimoT === null ? 0 : Math.min(t - ultimoT, 100); // tope: volver de otra pestaña no salta
      ultimoT = t;
      if (!enBucle || reducido() || quieto() || dt === 0) return;
      if (el.style.scrollSnapType !== 'none') { snap(false); pos = el.scrollLeft; }
      pos += (VELOCIDAD_PX_S * dt) / 1000;
      const w = anchoJuego();
      if (w > 0 && pos >= w) pos -= w;
      el.scrollLeft = pos;
    };
    if (enBucle) raf = requestAnimationFrame(paso);

    // Si lo movió la persona (dedo, rueda, snap), la posición propia se alinea
    // con la real; y si pasó al juego de copias, vuelve al original.
    const alScroll = () => {
      if (Math.abs(el.scrollLeft - pos) > 2) pos = el.scrollLeft;
      const w = anchoJuego();
      if (w > 0 && el.scrollLeft >= w) { el.scrollLeft -= w; pos = el.scrollLeft; }
    };

    // Mouse encima. Solo mouse: en el teléfono el toque dispara un "encima" que
    // después no se va, y el carrusel quedaría parado para siempre.
    const entra = (e) => { if (e.pointerType === 'mouse') estado.mouse = true; };
    const sale = (e) => { if (e.pointerType === 'mouse') estado.mouse = false; };

    // Foco de teclado: para y trae la tarjeta a la vista.
    const conFoco = (e) => {
      estado.foco = true;
      const item = e.target.closest('[data-item-carrusel]');
      if (item) item.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: reducido() ? 'auto' : 'smooth' });
    };
    const sinFoco = (e) => { if (!el.contains(e.relatedTarget)) estado.foco = false; };

    // Dedo: para mientras toca; si desliza, el snap acomoda al soltar.
    let toqueX = 0;
    const tocaInicio = (e) => { estado.toque = true; toqueX = e.touches[0].clientX; };
    const tocaMueve = (e) => { if (Math.abs(e.touches[0].clientX - toqueX) > UMBRAL_ARRASTRE_PX) snap(true); };
    const tocaFin = () => { estado.toque = false; estado.esperaHasta = performance.now() + ESPERA_TRAS_TOQUE_MS; };

    // Arrastre con el mouse. Más de UMBRAL px de movimiento cancela el click:
    // la tarjeta es un enlace y soltar sobre ella no tiene que navegar.
    let arrastre = null;
    let cancelarClick = false;
    const bajaMouse = (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      cancelarClick = false;
      arrastre = { x: e.clientX, desde: el.scrollLeft, activo: false };
    };
    const mueveMouse = (e) => {
      if (!arrastre) return;
      const dx = e.clientX - arrastre.x;
      if (!arrastre.activo) {
        if (Math.abs(dx) <= UMBRAL_ARRASTRE_PX) return;
        arrastre.activo = true; estado.arrastre = true; snap(false); el.style.cursor = 'grabbing';
      }
      const w = anchoJuego();
      let destino = arrastre.desde - dx;
      if (w > 0) {
        while (destino < 0) { destino += w; arrastre.desde += w; }
        while (destino >= w) { destino -= w; arrastre.desde -= w; }
      }
      el.scrollLeft = destino; pos = destino;
    };
    const subeMouse = () => {
      if (!arrastre) return;
      if (arrastre.activo) { cancelarClick = true; estado.arrastre = false; el.style.cursor = ''; snap(true); }
      arrastre = null;
    };
    const alClick = (e) => {
      if (!cancelarClick) return;
      cancelarClick = false;
      e.preventDefault(); e.stopPropagation();
    };

    // Rueda vertical: mueve el carrusel (de a una tarjeta, por el snap). Si no
    // hay bucle y ya está en un borde, deja pasar la rueda a la página.
    const rueda = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) { snap(true); return; } // trackpad horizontal: nativo
      const w = anchoJuego();
      const max = el.scrollWidth - el.clientWidth;
      if (!w && ((e.deltaY < 0 && el.scrollLeft <= 0) || (e.deltaY > 0 && el.scrollLeft >= max - 1))) return;
      e.preventDefault();
      if (w > 0 && e.deltaY < 0 && el.scrollLeft < 1) { el.scrollLeft += w; pos = el.scrollLeft; }
      snap(true);
      el.scrollBy({ left: e.deltaY, behavior: reducido() ? 'auto' : 'smooth' });
    };

    const alCambiarMovimiento = () => snap(!enBucle || reducido());

    el.addEventListener('scroll', alScroll, { passive: true });
    el.addEventListener('pointerenter', entra);
    el.addEventListener('pointerleave', sale);
    el.addEventListener('focusin', conFoco);
    el.addEventListener('focusout', sinFoco);
    el.addEventListener('touchstart', tocaInicio, { passive: true });
    el.addEventListener('touchmove', tocaMueve, { passive: true });
    el.addEventListener('touchend', tocaFin);
    el.addEventListener('touchcancel', tocaFin);
    el.addEventListener('pointerdown', bajaMouse);
    window.addEventListener('pointermove', mueveMouse);
    window.addEventListener('pointerup', subeMouse);
    el.addEventListener('click', alClick, true);
    el.addEventListener('wheel', rueda, { passive: false });
    if (mq && mq.addEventListener) mq.addEventListener('change', alCambiarMovimiento);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', alScroll);
      el.removeEventListener('pointerenter', entra);
      el.removeEventListener('pointerleave', sale);
      el.removeEventListener('focusin', conFoco);
      el.removeEventListener('focusout', sinFoco);
      el.removeEventListener('touchstart', tocaInicio);
      el.removeEventListener('touchmove', tocaMueve);
      el.removeEventListener('touchend', tocaFin);
      el.removeEventListener('touchcancel', tocaFin);
      el.removeEventListener('pointerdown', bajaMouse);
      window.removeEventListener('pointermove', mueveMouse);
      window.removeEventListener('pointerup', subeMouse);
      el.removeEventListener('click', alClick, true);
      el.removeEventListener('wheel', rueda);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', alCambiarMovimiento);
    };
    // services cambia de identidad en cada render de la página: el efecto solo
    // depende de cuántas son (los anchos los mide en vivo).
  }, [enBucle, services.length]);

  const items = enBucle ? [...services, ...services] : services;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ ...tituloSx, mb: 0 }}>{titulo}</Typography>
        {seMueve && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography aria-hidden="true" data-estado-carrusel=""
              sx={{ fontFamily: font, fontSize: 12, fontWeight: 600, color: '#7a93a4' }}>
              {pausado ? 'Pausado' : 'En movimiento'}
            </Typography>
            <Button onClick={alternarPausa} size="small" variant="outlined"
              aria-label={pausado ? `Reanudar el desplazamiento de ${titulo}` : `Pausar el desplazamiento de ${titulo}`}
              startIcon={pausado ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              sx={{ fontFamily: font, fontWeight: 700, fontSize: 12, textTransform: 'none', color: '#163244',
                borderColor: 'rgba(22,50,68,0.35)', borderRadius: '999px', py: 0.25,
                '&:hover': { borderColor: '#E7A432', bgcolor: 'transparent' },
                '&.Mui-focusVisible': { outline: '2px solid #E7A432', outlineOffset: 2 } }}>
              {pausado ? 'Reanudar' : 'Pausar'}
            </Button>
          </Box>
        )}
      </Box>
      <Box
        ref={ref}
        role="region"
        aria-roledescription="carrusel"
        aria-label={titulo}
        onDragStart={(e) => e.preventDefault()}
        sx={{
          display: 'flex', gap: `${GAP_PX}px`,
          overflowX: 'auto', overflowY: 'hidden',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          overscrollBehaviorX: 'contain',
          cursor: { md: 'grab' },
          userSelect: 'none',
          position: 'relative',
        }}
      >
        {items.map((s, i) => {
          const copia = i >= services.length;
          return (
            <Box
              key={`${s.id}-${copia ? 'c' : 'o'}`}
              data-item-carrusel=""
              ref={(n) => {
                if (copia && i === services.length) primeraCopia.current = n;
                if (n && copia) n.setAttribute('inert', '');
              }}
              aria-hidden={copia ? 'true' : undefined}
              sx={{ flex: anchoItem, scrollSnapAlign: 'start', minWidth: 0 }}
            >
              <Tarjeta servicio={s} variante="destacado" esAdmin={esAdmin} copia={copia}
                onEditar={onEditar} onEliminar={onEliminar} />
            </Box>
          );
        })}
      </Box>
    </>
  );
}
